import React from "react";
import { motion } from "framer-motion";
import { Boxes, Truck, Smartphone, Megaphone, Users } from "lucide-react";

const utilizationAreas = [
  {
    icon: <Boxes size={24} />,
    title: "Inventory Expansion",
    description:
      "More genres, languages, and formats to reach every kind of reader.",
    color: "bg-indigo-100 text-indigo-600",
    percentage: "35%",
     colorHex: "#6366f1"
  },
  {
    icon: <Truck size={24} />,
    title: "Logistics & Delivery",
    description: "Faster, smarter delivery across metro and rural India.",
    color: "bg-green-100 text-green-600",
    percentage: "25%",
     colorHex: "#16a34a"
  },
  {
    icon: <Smartphone size={24} />,
    title: "Technology Upgrades",
    description:
      "Enhancing AI recommendations, tracking, and mobile app experience.",
    color: "bg-blue-100 text-blue-600",
    percentage: "20%",
    colorHex: "#2563eb" 
  },
  {
    icon: <Megaphone size={24} />,
    title: "Marketing Campaigns",
    description:
      "Boosting regional and national visibility for the reading revolution.",
    color: "bg-pink-100 text-pink-600",
    percentage: "12%",
     colorHex: "#ec4899"
  },
  {
    icon: <Users size={24} />,
    title: "Team Building",
    description:
      "Hiring and training a mission-driven team to scale operations.",
    color: "bg-yellow-100 text-yellow-600",
    percentage: "8%",
    colorHex: "#eab308"
  },
];

const FundUtilizationSection = () => {
  return (
    <section className="relative py-24 px-6 md:px-12 bg-white overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-primary/5 to-transparent -z-0"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-indigo-100/30 blur-3xl -z-0"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4 tracking-wider">
            CAPITAL ALLOCATION
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Strategic <span className="text-primary">Fund Utilization</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Every rupee fuels impact. Here's where your investment will drive
            the most change.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
        <motion.div
  className="lg:w-2/5 bg-white/80 backdrop-blur-md rounded-3xl p-8 border border-gray-200 shadow-md hover:shadow-2xl transition-all"
  initial={{ opacity: 0, x: -30 }}
  whileInView={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.6 }}
  viewport={{ once: true }}
>
  <h3 className="text-2xl font-bold text-gray-900 mb-6 tracking-tight">
    Investment Allocation
  </h3>

  {/* Pie Ring Chart */}
  <div className="relative w-full h-80 flex items-center justify-center">
    <div
      className="w-64 h-64 rounded-full shadow-inner animate-spin-slow"
      style={{
        background: `conic-gradient(
          #6366f1 0% 35%,          /* Inventory Expansion */
          #16a34a 35% 60%,         /* Logistics */
          #2563eb 60% 80%,         /* Tech */
          #ec4899 80% 92%,         /* Marketing */
          #eab308 92% 100%         /* Team */
        )`,
      }}
    />
    <div className="absolute w-36 h-36 bg-white rounded-full shadow-md flex items-center justify-center">
      <span className="text-3xl font-extrabold text-primary">100%</span>
    </div>
  </div>

  {/* Legend */}
  <div className="mt-8 grid grid-cols-2 gap-4 text-sm text-gray-800">
    {utilizationAreas.map((item, idx) => (
      <div
        key={idx}
        className="flex items-center space-x-3 transition hover:scale-[1.02]"
      >
        <div
          className="w-4 h-4 rounded-full border border-white shadow-md"
          style={{ backgroundColor: item.colorHex }}
        ></div>
        <span className="font-medium">{item.title}</span>
      </div>
    ))}
  </div>
</motion.div>


          {/* Right side - detailed breakdown */}
          <div className="lg:w-3/5 grid md:grid-cols-2 gap-6">
            {utilizationAreas.map((item, idx) => (
              <motion.div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div
                  className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"
                  style={{ backgroundColor: item.color.split(" ")[1] }}
                ></div>

                <div className="relative z-10 p-6 h-full flex flex-col">
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${item.color} mb-4`}
                  >
                    {item.icon}
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <span className="text-sm font-bold px-3 py-1 rounded-full bg-primary/10 text-primary">
                        {item.percentage}
                      </span>
                    </div>

                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          className="mt-20 bg-gradient-to-r from-primary to-primary-shade4 rounded-2xl p-10 text-white shadow-xl"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Transparent Financial Management
            </h3>
            <p className="text-primary-shade1 mb-6 text-lg">
              We provide quarterly reports on fund utilization and impact
              metrics to all investors.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors shadow-md hover:shadow-lg">
                View Sample Report
              </button>
              <button className="bg-transparent border-2 border-white/30 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/10 transition-colors">
                Request Financial Details
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FundUtilizationSection;
